// apps/web/src/components/AdminShell.tsx — Phase 2 plan 02-04 + Phase 5 plan 05-05
// + Quick 260803-ger (D-01).
//
// Responsive admin application shell around /admin/*.
//
// At 375 px the layout is a stacked flex column: a fixed bottom-nav
// (2 columns now: Reservierungen + Einstellungen per 260803-ger D-01)
// sticks to the viewport edge and the child content scrolls behind
// it. At md (≥ 768 px) the bottom-nav is hidden and a 240 px
// (`w-60`) left sidebar takes its place.
//
// The Heute and Kalender tabs are gone — the merged
// /admin/reservierungen surface replaces both. The shell mounts
// exactly two NavLinks: Reservierungen and Einstellungen.

import type { ReactNode } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TAB_ROUTES: Record<string, string> = {
  tabReservierungen: "/admin/reservierungen",
  tabEinstellungen: "/admin/einstellungen",
};

interface TabDef {
  key: string;
}

export function AdminShell({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();

  // Single source of truth for both layouts. 260803-ger D-01: exactly
  // two tabs after the Heute+Kalender retirement.
  const tabs: TabDef[] = [
    { key: "tabReservierungen" },
    { key: "tabEinstellungen" },
  ];

  return (
    <div className="min-h-screen bg-bg text-text font-body flex flex-col md:flex-row">
      <aside
        data-testid="admin-sidebar"
        className="hidden md:block w-60 shrink-0 border-r border-divider bg-bg"
      >
        <div className="px-6 py-6 border-b border-divider">
          <span
            className="font-heading text-2xl text-text"
            data-testid="admin-sidebar-brand"
          >
            {t("nav.brand")}
          </span>
        </div>
        <nav aria-label="Admin navigation" className="px-3 py-4">
          <ul className="flex flex-col gap-1">
            {tabs.map((tab) => (
              <TabItem
                key={tab.key}
                label={t(`nav.${tab.key}`)}
                to={TAB_ROUTES[tab.key]!}
              />
            ))}
          </ul>
        </nav>
      </aside>

      <main
        data-testid="admin-content"
        className="min-w-0 flex-1 pb-20 md:pb-0"
      >
        {children ?? <Outlet />}
      </main>

      <nav
        data-testid="admin-bottom-nav"
        aria-label="Admin navigation"
        className="md:hidden fixed bottom-0 inset-x-0 z-20 border-t border-divider bg-bg"
      >
        <ul className="grid grid-cols-2">
          {tabs.map((tab) => (
            <TabItem
              key={tab.key}
              label={t(`nav.${tab.key}`)}
              to={TAB_ROUTES[tab.key]!}
              variant="bottom"
            />
          ))}
        </ul>
      </nav>
    </div>
  );
}

function TabItem({
  label,
  to,
  variant,
}: {
  label: string;
  to: string;
  variant?: "bottom";
}) {
  const baseClass =
    variant === "bottom"
      ? "flex items-center justify-center py-3 text-xs"
      : "flex items-center px-4 py-3 text-sm";

  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          `${baseClass} hover:bg-surface ${isActive ? "text-accent font-medium" : "text-text"}`
        }
        data-testid={`admin-tab-${slugify(label)}`}
      >
        {label}
      </NavLink>
    </li>
  );
}

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
